import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { ActionDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { ModifyDashboardView } from '@futrli/views/views/reports/ModifyDashboardView/ModifyDashboardView';
import { useApiView } from '@futrli/views/hooks/useApiView';

interface AddToPerformanceAreaDialogProps {
  dashboardId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const AddToPerformanceAreaDialog = ({ dashboardId, isOpen, onClose }: AddToPerformanceAreaDialogProps) => {
  const intl = useIntl();
  const { workspaceId = '', organisationId = '' } = useParams();

  const view = useApiView(
    useMemo(() => new ModifyDashboardView(workspaceId, organisationId, dashboardId), [
      workspaceId,
      organisationId,
      dashboardId
    ])
  );

  const dashboardName = view.state.dashboard?.name ?? '';

  const { screen, handleAction, handleClose } = useDialogWizardState({
    isLoading: view.state.isLoading || view.state.isAddingToPerformanceArea,
    error: view.state.errorAddingToPerformanceArea,
    onAction: () => view.addToPerformanceArea(dashboardName),
    onClose
  });

  const title = intl.formatMessage({
    id: 'predict.dashboards.add_to_performance_area',
    defaultMessage: 'Add to performance area'
  });

  return (
    <ActionDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleAction}
      screen={screen}
      title={title}
      subtitle={view.state.dashboard?.name}
      confirmTitle={intl.formatMessage({ id: 'common.confirm', defaultMessage: 'Confirm' })}
      confirmDescription={intl.formatMessage({
        id: 'predict.dashboards.add_to_performance_area_desc',
        defaultMessage: 'All users will have access to this dashboard from the performance dashboards tab'
      })}
      loadingMessage={intl.formatMessage({
        id: 'predict.dashboards.adding_to_performance_area',
        defaultMessage: 'Adding to performance area...'
      })}
      successMessage={intl.formatMessage({
        id: 'predict.dashboards.dashboard_added_to_performance_area',
        defaultMessage: 'Dashboard added to performance area'
      })}
      errorMessage={view.state.errorAddingToPerformanceArea?.message}
      dataTestId="add-to-performance-area-dialog"
    />
  );
};
